import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import '../../App.css';
import { FaCheckCircle, FaTimesCircle, FaUserCircle, FaLock, FaEye, FaEyeSlash } from 'react-icons/fa';
import { motion } from 'framer-motion';

/*
  CompleteInvite
  Página para que un usuario invitado (correo enviado vía invite_user_by_email) termine su registro definiendo contraseña.
  Flujo esperado:
    1. El usuario hace clic en el enlace del correo (tiene access_token o tipo recovery/invite en parámetros hash).
    2. Supabase valida el token y ya hay una sesión temporal disponible o se puede recuperar.
    3. El usuario elige su contraseña y la confirmación.
    4. Se llama supabase.auth.updateUser({ password }) para fijarla.
    5. Redirige a /login para iniciar sesión normalmente.

  Notas:
    - Si el token ha expirado se muestra error y se invita a pedir nueva invitación.
    - Se hace una verificación simple de la sesión inicial.
*/

const CompleteInvite = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [email, setEmail] = useState('');
  const [strength, setStrength] = useState(0);
  const [valids, setValids] = useState({ len: false, upper: false, num: false, sym: false });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const secureOk = valids.len && valids.upper && valids.num && valids.sym;

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      setError(null);
      try {
        // Supabase detecta automáticamente tokens en URL hash (#access_token=...)
        // y actualiza la sesión. Esperamos para que procese.
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        const { data, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) throw sessionError;
        
        if (!data.session) {
          // Si no hay sesión, el token puede estar expirado o el link es inválido
          setError('El enlace no es válido o ha expirado. Solicita una nueva invitación al administrador.');
        } else {
          setEmail(data.session.user.email);
          
          // NO redirigir automáticamente, dejar que el usuario establezca su contraseña
          // (el email_confirmed_at puede estar presente por el invite, pero aún necesita password)
        }
      } catch (e) {
        setError('Error al validar la invitación: ' + (e.message || String(e)));
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [searchParams]);

  useEffect(() => {
    const s = password || '';
    const v = {
      len: s.length >= 8,
      upper: /[A-Z]/.test(s),
      num: /[0-9]/.test(s),
      sym: /[^A-Za-z0-9]/.test(s)
    };
    setValids(v);
    setStrength(Object.values(v).filter(Boolean).length);
  }, [password]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!password || !confirm) {
      setError('Debes ingresar y confirmar la contraseña.');
      return;
    }
    if (password !== confirm) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    if (!(valids.len && valids.upper && valids.num && valids.sym)) {
      setError('La contraseña no cumple los requisitos.');
      return;
    }

    setSubmitting(true);
    try {
      const { error: updError } = await supabase.auth.updateUser({ password });
      if (updError) {
        throw updError;
      }
      setSuccess(true);
      // Mensaje de éxito y redirigir al login
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (e) {
      setError(e.message || 'No se pudo establecer la contraseña');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⏳</div>
          <h2>Validando tu invitación...</h2>
          <p style={{ color: '#64748b' }}>Espera un momento</p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page-container">
      <motion.div className="relative bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl p-8 w-full max-w-md border border-white/20" initial={{ opacity: 0, y: -30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} style={{ zIndex: 10 }}>
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl shadow-lg shadow-orange-500/50 mb-4">
            <FaUserCircle size={48} color="#FFFFFF" />
          </div>
          <h2 className="text-2xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent mb-2">Establece tu contraseña</h2>
          {email && <p className="text-slate-600 font-medium">Cuenta: <span className="text-slate-800 font-semibold">{email}</span></p>}
        </div>
        
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm font-medium mb-4">
            {error}
          </div>
        )}
        
        {success ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-emerald-700 text-sm font-medium mb-4 text-center">¡Contraseña establecida correctamente! Redirigiendo…</div>
        ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-slate-700">Contraseña</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none"><FaLock className="text-slate-400" /></div>
              <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e)=>setPassword(e.target.value)} placeholder="Mínimo 8 caracteres" disabled={submitting} className="w-full pl-12 pr-12 py-3 bg-slate-50 border border-slate-300 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all disabled:opacity-60 disabled:cursor-not-allowed" />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-slate-600 focus:outline-none">
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            <div className="mt-2">
              <div className="h-2 rounded-full" style={{ background: '#e2e8f0' }}>
                <div className="h-2 rounded-full transition-all" style={{ width: `${(strength/4)*100}%`, background: strength < 2 ? '#ef4444' : strength < 3 ? '#f59e0b' : '#22c55e' }} />
              </div>
              <div className="grid grid-cols-2 gap-2 mt-3 text-sm">
                <div className={`${valids.len ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'} inline-flex items-center gap-2 px-2.5 py-1 rounded-full`}> <span className={`${valids.len ? 'text-emerald-600' : 'text-slate-500'}`}>{valids.len ? <FaCheckCircle /> : <FaTimesCircle />}</span> Mínimo 8 caracteres</div>
                <div className={`${valids.upper ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'} inline-flex items-center gap-2 px-2.5 py-1 rounded-full`}> <span className={`${valids.upper ? 'text-emerald-600' : 'text-slate-500'}`}>{valids.upper ? <FaCheckCircle /> : <FaTimesCircle />}</span> Mayúscula</div>
                <div className={`${valids.num ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'} inline-flex items-center gap-2 px-2.5 py-1 rounded-full`}> <span className={`${valids.num ? 'text-emerald-600' : 'text-slate-500'}`}>{valids.num ? <FaCheckCircle /> : <FaTimesCircle />}</span> Número</div>
                <div className={`${valids.sym ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'} inline-flex items-center gap-2 px-2.5 py-1 rounded-full`}> <span className={`${valids.sym ? 'text-emerald-600' : 'text-slate-500'}`}>{valids.sym ? <FaCheckCircle /> : <FaTimesCircle />}</span> Símbolo</div>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-slate-700">Confirmar contraseña</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none"><FaLock className="text-slate-400" /></div>
              <input type={showConfirm ? 'text' : 'password'} value={confirm} onChange={(e)=>setConfirm(e.target.value)} placeholder="••••••••" disabled={submitting} className="w-full pl-12 pr-12 py-3 bg-slate-50 border border-slate-300 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all disabled:opacity-60 disabled:cursor-not-allowed" />
              <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-slate-600 focus:outline-none">
                {showConfirm ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>
          <button type="submit" disabled={submitting || !secureOk || password !== confirm} className="w-full py-3 px-6 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold rounded-xl shadow-lg shadow-orange-500/50 transition-all disabled:opacity-60 disabled:cursor-not-allowed">{submitting ? 'Estableciendo...' : 'Establecer contraseña'}</button>
        </form>
      )}
      {!success && !error && (
        <p className="mt-4 text-center text-sm text-slate-500">Después de guardar tu contraseña serás redirigido al inicio de sesión</p>
      )}
      </motion.div>
    </div>
  );
};

export default CompleteInvite;
